import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

// Renders "12 Aug 2026 · 1:04 PM" from a Go RFC3339 timestamp. Kept small
// and dependency-free (moment isn't used in this section).
const _MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const formatStamp = (iso) => {
  if (!iso) return { date: "—", time: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "—", time: "" };
  const dd = d.getDate();
  const mm = _MONTHS[d.getMonth()];
  const yy = d.getFullYear();
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return { date: `${dd} ${mm} ${yy}`, time: `${h}:${m} ${ampm}` };
};

// Rollup label from CapturedByStatus. `in-progress` gets a live line; ended
// transcripts summarize the added-vs-reviewed counts + total call duration
// (endedAt − startedAt, formatted MM:SS via the same helper the live timer
// uses).
const detailFor = (t) => {
  const r = t.capturedByStatus || {};
  const totalPending = (r.addedInCall || 0) + (r.editedInCall || 0);
  const totalSaved = r.reviewedAndSaved || 0;
  if (t.status === "in-progress") {
    return "In progress · AI is capturing…";
  }
  const dur = t.endedAt ? durationLabel(t.startedAt, t.endedAt) : "";
  if (t.status === "reviewed") {
    return `Reviewed · ${totalSaved} items saved`
      + (dur ? ` · ${dur}` : "");
  }
  // pending
  if (totalPending === 0 && totalSaved === 0) {
    return "Ended" + (dur ? ` · ${dur}` : "") + " · nothing to review";
  }
  const parts = [];
  if (dur) parts.push(dur);
  if (totalPending) parts.push(`${totalPending} pending`);
  if (totalSaved) parts.push(`${totalSaved} saved`);
  return `Ended · ${parts.join(" · ")}`;
};

const badgeFor = (status) => {
  if (status === "in-progress") return { cls: "is-live", text: "LIVE" };
  if (status === "reviewed") return { cls: "is-reviewed", text: "REVIEWED" };
  return { cls: "is-pending", text: "PENDING" };
};

/**
 * The 260w LEFT rail of the AI transcript modal. It renders differently per
 * state, driven by the `state` prop:
 *   s0 — the "Transcripts" list (2618:3498)
 *   s1 — calling / ringing, waiting for pickup (2526:3417)
 *   s2/s3 — LIVE transcript chat + In-review / AI-confidence stat tiles
 *           (2526:12196 / 2495:3060)
 *   s4 — call ended + a maroon-gradient "Save and Next" CTA (2495:3487)
 * UI-only: the transition callbacks are wired by AiTranscriptModal; nothing here
 * talks to telephony.
 */

/** Small circular X used on the rail status row. */
const RailClose = ({ onClick }) => (
  <button type="button" className="ait-rail-x" onClick={onClick} aria-label="Close call">
    &times;
  </button>
);
RailClose.propTypes = { onClick: PropTypes.func };

// Normalise the Go model.Turn shape into what the chat renderer expects.
// Go emits `speaker: "care_manager" | "patient"`; the existing CSS pairs
// with `is-care` / `is-patient` — map accordingly.
const speakerCls = (s) => (s === "care_manager" ? "care" : "patient");
const speakerLabel = (s) => (s === "care_manager" ? "Care manager" : "Patient");

/** Format elapsed seconds → "MM:SS" for the rail's duration counter. */
const durationLabel = (startedAt, endedAt) => {
  if (!startedAt) return "0:00";
  const start = new Date(startedAt);
  const end   = endedAt ? new Date(endedAt) : new Date();
  const secs  = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000));
  const m = Math.floor(secs / 60);
  const s = String(secs % 60).padStart(2, "0");
  return `${m}:${s}`;
};

// ─── Entity pills + Added-to-form annotations ────────────────────
//
// We drive both from the captured items — each item carries its raw
// `evidence` (the verbatim STT substring the LLM quoted) and a
// `payload` with the extracted field values. All the string fields we
// care about (complaint, medication, since_when, intensity, ...) are
// verbatim slices of the evidence per the agent's evidence-guard rule,
// so a plain substring search reliably finds their offsets in the turn
// text.
//
// The Turn.Entities[] and Turn.AddedRefs[] fields on the Go model are
// deliberately left blank by the agent — this component derives the
// same information from the captured items already in the transcript
// document, so no agent-side plumbing is needed.

// Field name → CSS class + palette. Colors match the "AI transcript —
// entity color code" spec: one hue per clinical entity, shared by inline
// pills AND the "Added to form" chip.
const FIELD_STYLE = {
  // Primary concept fields — colored by section (see PRIMARY_SECTION_STYLE).
  complaint:      { primary: true },
  name:           { primary: true },
  condition:      { primary: true },
  substance:      { primary: true },
  // Category-driven modifiers (independent of section).
  medication:     "e-med",
  dose_qty:       "e-med",
  formulation:    "e-med",
  intensity:      "e-severity",
  vas_band:       "e-severity",
  since_when:     "e-temporal",
  since_date:     "e-temporal",
  frequency:      "e-temporal",
  time_period:    "e-temporal",
  test_date:      "e-temporal",
  start_date:     "e-temporal",
  end_date:       "e-temporal",
  follow_up_date: "e-temporal",
  date_of_surgery:   "e-temporal",
  date_of_discharge: "e-temporal",
  description:    "e-detail",
  // Vitals — BP, HR, SpO2, temp, and the numeric anthropometric fields
  // (weight, height, BMI) get the same orange pill.
  bp:             "e-vital",
  blood_pressure: "e-vital",
  heart_rate:     "e-vital",
  spo2:           "e-vital",
  temperature:    "e-vital",
  weight:         "e-vital",
  height:         "e-vital",
  bmi:            "e-vital",
  waist:          "e-vital",
  hip:            "e-vital",
  // Lab — a sub-test value + units render as one lab pill.
  result_value:   "e-lab",
  result_units:   "e-lab",
  ref_range:      "e-lab",
  // Procedure — CPT-linked slot for hospitalisation surgeries.
  procedure:      "e-procedure",
  cpt_description: "e-procedure",
  // Treatment / resolution status verbs.
  treatment_status: "e-status",
  status:         "e-status",
};

// Which pill class a section's "primary concept" field uses. Reused by
// the "Added to form" chip so the chip matches the pill on the sentence
// above it. Sections without a distinct entity color fall back to blue
// so the chip is at least visually consistent within the transcript.
const PRIMARY_SECTION_STYLE = {
  chief_complaints:  "e-primary",   // blue    — chief complaint / symptom
  diagnosis:         "e-diagnosis", // purple  — ICD-linked condition
  medicines:         "e-med",       // yellow  — medication
  hospitalisation:   "e-procedure", // teal    — surgery / procedure (CPT)
  allergies:         "e-allergy",   // red     — allergy / adverse reaction
  lab_investigations: "e-lab",      // green   — lab value
  anthropometric:    "e-vital",     // orange  — vitals / body measurements
  family_history:    "e-primary",
  addictions:        "e-primary",
  food_preferences:  "e-primary",
  daily_schedule:    "e-primary",
  activity:          "e-primary",
  drug_schedule:     "e-primary",
};

const SECTION_SHORT = {
  chief_complaints:  "Chief complaint",
  diagnosis:         "Diagnosis",
  medicines:         "Medications",
  hospitalisation:   "Hospitalisation",
  allergies:         "Allergy",
  family_history:    "Family history",
  addictions:        "Addiction",
  anthropometric:    "Anthropometric",
  lab_investigations: "Lab investigation",
  food_preferences:  "Food preference",
  daily_schedule:    "Daily schedule",
  activity:          "Activity",
  drug_schedule:     "Drug schedule",
};

// Per-section anchoring code field + code-system prefix. When the item's
// payload carries the field, the chip renders "<Section> · <PREFIX> <code>"
// (e.g. "Chief complaint · ICD R51"). Sections without a coded anchor
// (allergies, family_history, addictions, …) render just the section
// name — the map lookup returns undefined and the chip omits the code
// suffix span.
const SECTION_CODE_FIELD = {
  chief_complaints:  { field: "icd_code",   label: "ICD" },
  diagnosis:         { field: "icd_code",   label: "ICD" },
  hospitalisation:   { field: "cpt_code",   label: "CPT" },
  medicines:         { field: "atc_code",   label: "ATC" },
  lab_investigations: { field: "loinc_code", label: "LOINC" },
};

// Find every non-overlapping match of `needle` in `haystack`. Returns
// [{start, end}] using byte offsets. Case-insensitive to accommodate
// the STT / LLM sometimes capitalising differently.
const findAllOccurrences = (haystack, needle) => {
  if (!haystack || !needle || needle.length < 2) return [];
  const H = haystack.toLowerCase();
  const N = needle.toLowerCase();
  const out = [];
  let idx = 0;
  while (idx <= H.length - N.length) {
    const at = H.indexOf(N, idx);
    if (at < 0) break;
    out.push({ start: at, end: at + N.length });
    idx = at + N.length;
  }
  return out;
};

// Build the entity ranges for a single turn given the captured items
// that were extracted from it. Overlapping ranges resolve to the
// EARLIEST + LONGEST — we drop the shorter one so the pills don't
// visually clash.
const buildTurnEntities = (turnText, ownedItems) => {
  const ranges = []; // {start, end, cls}
  for (const item of ownedItems) {
    const payload = (item && item.payload) || {};
    for (const [field, value] of Object.entries(payload)) {
      if (typeof value !== "string" || !value.trim()) continue;
      const style = FIELD_STYLE[field];
      if (!style) continue;
      const cls = style.primary
        ? (PRIMARY_SECTION_STYLE[item.targetSection] || "e-primary")
        : style;
      for (const r of findAllOccurrences(turnText, value)) {
        ranges.push({ start: r.start, end: r.end, cls });
      }
    }
  }
  // Sort by start, drop overlaps (keep the earliest — if ties, longest).
  ranges.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
  const merged = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (!last || r.start >= last.end) merged.push(r);
  }
  return merged;
};

// Split a turn's text into React nodes, wrapping each entity span in a
// pill. Segments between entities render as plain text.
const renderTurnText = (text, entities) => {
  if (!entities.length) return text;
  const nodes = [];
  let cursor = 0;
  entities.forEach((r, i) => {
    if (cursor < r.start) nodes.push(text.slice(cursor, r.start));
    nodes.push(
      // eslint-disable-next-line react/no-array-index-key
      <span className={`ait-t2-pill ${r.cls}`} key={i}>
        {text.slice(r.start, r.end)}
      </span>,
    );
    cursor = r.end;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
};

/**
 * Live transcript chat body (s2/s3/s4). No message bubbles — plain
 * speaker label (small caps) + text. Entity pills + "Added to form"
 * annotations are derived from `capturedItems`.
 */
const TranscriptChat = ({ turns, capturedItems }) => {
  const scrollRef = useRef(null);
  const lastLen = useRef(0);
  useEffect(() => {
    if (turns.length > lastLen.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    lastLen.current = turns.length;
  }, [turns.length]);

  return (
    <div className="ait-chat2" ref={scrollRef}>
      <p className="ait-chat2-eyebrow">
        <span className="ait-chat2-dot" aria-hidden="true" />
        Live transcript
      </p>
      {turns.length === 0 ? (
        <p className="ait-chat2-empty">
          Listening… the transcript appears here as either side speaks.
        </p>
      ) : null}
      {turns.map((turn) => {
        // An item was extracted from this turn if its `evidence`
        // substring appears in the turn's text (case-insensitive).
        // Guards: any missing text / evidence just means no annotations
        // for that pair — we never want to blank the transcript because
        // of a shape hiccup.
        const text = (turn && typeof turn.text === "string") ? turn.text : "";
        const lowerText = text.toLowerCase();
        const owned = (capturedItems || []).filter((it) => {
          const ev = it && typeof it.evidence === "string" ? it.evidence : "";
          if (!ev) return false;
          return lowerText.includes(ev.toLowerCase());
        });
        const entities = buildTurnEntities(text, owned);
        return (
          <div className={`ait-t2 is-${speakerCls(turn.speaker)}`} key={turn.id}>
            <p className="ait-t2-speaker">{speakerLabel(turn.speaker).toUpperCase()}</p>
            <p className="ait-t2-text">{renderTurnText(text, entities)}</p>
            {owned.length > 0 ? (
              <div className="ait-t2-added">
                <span className="ait-t2-added-arrow" aria-hidden="true">↳</span>
                <span className="ait-t2-added-label">Added to form</span>
                {owned.map((it) => {
                  const cls =
                    PRIMARY_SECTION_STYLE[it.targetSection] || "e-primary";
                  const label = SECTION_SHORT[it.targetSection] || it.targetSection;
                  const codeSpec = SECTION_CODE_FIELD[it.targetSection];
                  const code = codeSpec
                    && it.payload
                    && typeof it.payload[codeSpec.field] === "string"
                    && it.payload[codeSpec.field].trim();
                  return (
                    <span key={it.id} className={`ait-t2-pill ${cls}`}>
                      {label}
                      {code ? (
                        <span className="ait-t2-pill-code">
                          &nbsp;&middot; {codeSpec.label} {code}
                        </span>
                      ) : null}
                    </span>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
TranscriptChat.propTypes = {
  turns: PropTypes.arrayOf(PropTypes.shape({
    id:      PropTypes.string.isRequired,
    speaker: PropTypes.string.isRequired,
    text:    PropTypes.string.isRequired,
  })).isRequired,
  capturedItems: PropTypes.array,
};
TranscriptChat.defaultProps = { capturedItems: [] };

const LiveCallPanel = ({
  state,
  patient,
  transcripts,
  loading,
  error,
  initError,
  callState,
  liveTurns,
  capturedItems,
  callStartedAt,
  callEndedAt,
  onSelectTranscript,
  onPlayRecording,
  onCancelCall,
  onSaveAndNext,
}) => {
  const name = patient.name;

  return (
    <aside className="ait-rail">
      {state === "s0" && (
        <div className="ait-rail-list">
          <div className="ait-rail-head">
            <h3 className="ait-rail-title">
              Transcripts
              {transcripts.length > 0 ? ` (${transcripts.length})` : ""}
            </h3>
          </div>

          {loading ? (
            <p className="ait-rail-status-text ait-rail-load">Loading…</p>
          ) : error ? (
            <p className="ait-rail-status-text ait-rail-load">
              Couldn’t load transcripts. Try again.
            </p>
          ) : transcripts.length === 0 ? (
            <p className="ait-rail-status-text ait-rail-load">
              No past transcripts. Start a call below.
            </p>
          ) : (
            <div className="ait-tx-list">
              {transcripts.map((t) => {
                const stamp = formatStamp(t.startedAt);
                const badge = badgeFor(t.status);
                // Recording play chip: only when the LK egress finished
                // AND document-service handed back a numeric recording
                // id (transcript.callRecording.recordingId > 0). Wrapped
                // as a nested clickable — stopPropagation on click so
                // the row's own onSelectTranscript doesn't fire.
                const rid = t.callRecording && t.callRecording.recordingId;
                const canPlay = !!rid && rid > 0 && !!onPlayRecording;
                return (
                  <button
                    type="button"
                    className="ait-tx"
                    key={t.id}
                    onClick={() => onSelectTranscript(t)}
                  >
                    <span className="ait-tx-row">
                      <span className="ait-tx-when">
                        {stamp.date}, {stamp.time}
                      </span>
                      <span className="ait-tx-row-right">
                        {canPlay ? (
                          <span
                            className="ait-tx-play"
                            role="button"
                            tabIndex={0}
                            title="Play recording"
                            onClick={(e) => { e.stopPropagation(); onPlayRecording(t); }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.stopPropagation();
                                onPlayRecording(t);
                              }
                            }}
                          >
                            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                              <path fill="currentColor" d="M8 5v14l11-7z" />
                            </svg>
                          </span>
                        ) : null}
                        <span className={`ait-badge ${badge.cls}`}>{badge.text}</span>
                      </span>
                    </span>
                    <span className="ait-tx-detail">{detailFor(t)}</span>
                  </button>
                );
              })}
            </div>
          )}

          <p className="ait-rail-help">
            Click a transcript to open its review. To start a new call use
            the <strong>Call patient</strong> button in the header.
          </p>
          {initError ? (
            <p className="ait-rail-status-text ait-rail-load">{initError}</p>
          ) : null}
        </div>
      )}

      {state === "s1" && (
        <div className="ait-rail-call">
          <div className="ait-rail-status">
            <span className="ait-rail-status-text">
              {callState === "ringing_patient"
                ? "CONNECTING PATIENT…"
                : "CONNECTING CARE MANAGER…"}
            </span>
            <RailClose onClick={onCancelCall} />
          </div>

          <div className="ait-call-stage">
            <span className="ait-call-phone" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path
                  d="M5 4h3l1.5 4-2 1.5a12 12 0 005.5 5.5L20 13l4 1.5V18a2 2 0 01-2 2A16 16 0 015 5a2 2 0 010-1z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <p className="ait-call-line">
              {callState === "ringing_patient"
                ? "Waiting for the patient to join."
                : "Connecting the care manager's first. Once connected, the"
                  + " patient's phone is dialled next."}
            </p>
          </div>

          <div className="ait-rail-actions">
            <button type="button" className="ait-rail-cta-danger" onClick={onCancelCall}>
              Cancel call
            </button>
          </div>
        </div>
      )}

      {(state === "s2" || state === "s3" || state === "s4") && (
        <div className="ait-rail-live">
          <div className="ait-rail-topline">
            {state === "s2" ? (
              <span className="ait-pill is-live"><span />LIVE</span>
            ) : (
              <span className="ait-pill is-ended"><span />ENDED</span>
            )}
            <span className="ait-rail-clock">
              {durationLabel(callStartedAt, callEndedAt)}
            </span>
          </div>
          <TranscriptChat turns={liveTurns} capturedItems={capturedItems} />
        </div>
      )}
    </aside>
  );
};

LiveCallPanel.propTypes = {
  /** Which state the modal is in — drives the whole rail. */
  state: PropTypes.oneOf(["s0", "s1", "s2", "s3", "s4"]).isRequired,
  patient: PropTypes.shape({
    name: PropTypes.string.isRequired,
    phone: PropTypes.string,
    pid: PropTypes.string,
  }).isRequired,
  /** S0 data. Rendered only when state === "s0". */
  transcripts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      startedAt: PropTypes.string,
      capturedByStatus: PropTypes.object,
    }),
  ),
  loading: PropTypes.bool,
  error: PropTypes.bool,
  /** Set when /initiate itself fails (network / auth / no CM phone). */
  initError: PropTypes.string,
  /** Server-side SIP call state — drives the S1 rail copy.
   *   ""                 no active call (S0)
   *   "ringing_cm"       ringing the care manager, waiting for pickup
   *   "ringing_patient"  CM answered, ringing the patient
   *   "connected"        both parties on the line (modal transitions to S2)
   *   "dropped"          either party hung up (modal transitions to S3)
   */
  callState: PropTypes.string,
  liveTurns: PropTypes.array,
  /** Feeds inline entity pills + "Added to form" chips on each turn. */
  capturedItems: PropTypes.array,
  /** Timestamps used for the rail's duration counter. */
  callStartedAt: PropTypes.string,
  callEndedAt:   PropTypes.string,
  onSelectTranscript: PropTypes.func.isRequired,
  /**
   * Click on the recording play chip on an S0 row. Fires with the row's
   * transcript object. Skipped when the transcript has no recording
   * (transcript.callRecording.recordingId falsy). Optional — chip is
   * hidden when this prop is absent.
   */
  onPlayRecording: PropTypes.func,
  onCancelCall: PropTypes.func.isRequired,
  onSaveAndNext: PropTypes.func.isRequired,
};

LiveCallPanel.defaultProps = {
  transcripts: [],
  loading: false,
  error: false,
  initError: "",
  callState: "",
  liveTurns: [],
  capturedItems: [],
  callStartedAt: "",
  callEndedAt:   "",
};

export default LiveCallPanel;
